import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { travelInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, Plane, ExternalLink, AlertCircle, Info } from 'lucide-react';

const TravelInsurance = () => {
  const { language } = useLanguage();
  const content = travelInsurancePage[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-700 to-cyan-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Plane className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-cyan-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* Reference Code Notice */}
      <section className="py-8 bg-yellow-50 border-b border-yellow-200">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-yellow-100 border-l-4 border-yellow-500 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-700 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-yellow-900 mb-2">
                    {language === 'en' ? 'Important - Reference Code' : 'Importante - Código de Referencia'}
                  </p>
                  <p className="text-yellow-800">
                    {content.note}
                  </p>
                  <p className="text-2xl font-bold text-yellow-900 mt-3">
                    {content.referenceCode}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Coverage Highlights' : 'Aspectos Destacados de Cobertura'}
            </h2>
            <div className="grid gap-4">
              {content.coverageHighlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <p className="text-base text-gray-700">{highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Plan Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Plan Options' : 'Opciones de Planes'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {content.planTypes.map((plan, index) => (
                <div key={index} className="bg-white border-2 border-cyan-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-cyan-800 mb-3">{plan.name}</h3>
                  <p className="text-gray-700">{plan.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />
                <h3 className="text-xl font-bold text-blue-900">
                  {language === 'en' ? 'Important Information' : 'Información Importante'}
                </h3>
              </div>
              <ul className="space-y-3 ml-9">
                {content.importantNotes.map((note, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-cyan-50 border-l-4 border-cyan-500 p-6 rounded-lg">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-700 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Get Your Travel Insurance Quote' : 'Obtenga Su Cotización de Seguro de Viaje'}
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              {language === 'en' 
                ? 'Visit our partner Redbridge Assist for detailed plans and instant quotes' 
                : 'Visite nuestro socio Redbridge Assist para planes detallados y cotizaciones instantáneas'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={content.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-cyan-50 transition-all shadow-lg"
              >
                <ExternalLink className="w-6 h-6" />
                {language === 'en' ? 'Get a Quote' : 'Obtener Cotización'}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-cyan-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-cyan-900 transition-all shadow-lg"
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TravelInsurance;